/**
 * Authentication store + axios interceptor + React hook.
 *
 * The JWT is kept in localStorage (key `kf_auth_token`) and injected into
 * every axios request as `Authorization: Bearer <token>`. A 401 response
 * automatically clears the token and forces a redirect to /login.
 *
 * The current user object is cached in memory and broadcast via
 * `kf:auth-change` so components subscribed via `useAuth()` re-render.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const TOKEN_KEY = 'kf_auth_token';
const USER_KEY  = 'kf_auth_user';
const API = process.env.REACT_APP_BACKEND_URL;

// ---- token + user state -------------------------------------------------
export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch (_) { return null; }
}

export function getStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

function setAuth(token, user) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch (_) { /* ignore */ }
  window.dispatchEvent(new CustomEvent('kf:auth-change', { detail: { token, user } }));
}

// ---- axios interceptors -------------------------------------------------
let _interceptorsInstalled = false;
export function installAxiosAuthInterceptors() {
  if (_interceptorsInstalled) return;
  _interceptorsInstalled = true;
  axios.interceptors.request.use((config) => {
    const t = getToken();
    if (t && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${t}`;
    }
    return config;
  });
  axios.interceptors.response.use(
    (r) => r,
    (err) => {
      const status = err?.response?.status;
      // Only react to 401 for /api routes — let the app handle 403s in-place.
      const url = err?.config?.url || '';
      if (status === 401 && url.includes('/api/') && !url.endsWith('/api/auth/login')) {
        setAuth(null, null);
        // Don't double-redirect if we're already on /login
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          // Preserve the page they were trying to reach.
          const next = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.replace(`/login?next=${next}`);
        }
      }
      return Promise.reject(err);
    },
  );
}

// ---- API calls ----------------------------------------------------------
export async function login(email, password) {
  const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
  setAuth(data.access_token, data.user);
  return data.user;
}

export async function logout() {
  try { await axios.post(`${API}/api/auth/logout`); } catch (_) { /* best-effort */ }
  setAuth(null, null);
}

export async function refreshMe() {
  try {
    const { data } = await axios.get(`${API}/api/auth/me`);
    setAuth(getToken(), data);
    return data;
  } catch (_) {
    setAuth(null, null);
    return null;
  }
}

// First-login password reset. The user got here by logging in with the
// admin-issued temp password; the bearer token they hold works fine, we
// just swap the password and clear the `must_change_password` gate.
// Returns a fresh JWT so the client can drop straight into the app.
export async function setInitialPassword(newPassword) {
  const { data } = await axios.post(`${API}/api/auth/set-initial-password`, {
    new_password: newPassword,
  });
  setAuth(data.access_token, data.user);
  return data.user;
}

// ---- React hook ---------------------------------------------------------
export function useAuth() {
  const [state, setState] = useState(() => ({
    token: getToken(),
    user: getStoredUser(),
  }));
  useEffect(() => {
    const on = () => setState({ token: getToken(), user: getStoredUser() });
    window.addEventListener('kf:auth-change', on);
    window.addEventListener('storage', on);
    return () => {
      window.removeEventListener('kf:auth-change', on);
      window.removeEventListener('storage', on);
    };
  }, []);
  return {
    token: state.token,
    user: state.user,
    isAuthenticated: !!state.token && !!state.user,
    isAdmin: state.user?.role === 'admin',
  };
}
